import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import { EmptyState } from '@/components/EmptyState';
import RefashionLogo from '@/components/RefashionLogo';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useShowToast } from '@/design_system/Toast';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconCheck from '@/icons/Check.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import { useActionTypesOrganization, useDeleteActionType } from '@/models/actionType';
import { useDatabaseTranslation } from '@/services/i18n';
import { unique } from '@/utils/array';

import { AgreementPricesDialog } from '../shared/AgreementPricesDialog';
import { ProductAttributeValuesHierarchy } from '../shared/ProductAttributeValuesHierarchy';

import { ActionDrawer } from './ActionDrawer';

export type ActionType =
  Endpoints['GET /action-types/organization']['response']['actionTypes'][number];

export type ActionDefectValue = {
  id: string;
  defectTypeId: string;
  mandatory: boolean | undefined;
  brandIds: string[];
};

export const ActionsTable = () => {
  const { t } = useLingui();
  const { _compare } = useDatabaseTranslation();

  const { data: { actionTypes } = {}, isLoading } = useActionTypesOrganization();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [actionTypeToEdit, setActionTypeToEdit] = useState<ActionType | null>(null);

  const sortedActionTypes = actionTypes?.toSorted((a, b) => _compare(a.name, b.name));

  const columnWidths = {
    name: 'minmax(400px, auto)',
    productCategories: 'minmax(300px, auto)',
    quantifiable: 'auto',
    linkedDefects: 'auto',
    childActions: 'auto',
    spareParts: 'auto',
    brands: 'auto',
    additionalFields: 'auto',
    editOrDelete: '120px',
  };

  return (
    <Stack gap="24px">
      <Stack row alignItems="center" justifyContent="space-between" gap="8px">
        <Title size="200" as="h2">
          <Trans id="settings.actions.actions-table.title">Actions</Trans>
        </Title>
        <Button onPress={() => setIsDrawerOpen(true)}>
          <IconAdd />
          <Trans id="settings.actions.actions-table.add">New action</Trans>
        </Button>
      </Stack>

      {!isLoading && !sortedActionTypes?.length ? (
        <EmptyState subtitle={<Trans id="settings.actions.actions-table.none">No actions</Trans>} />
      ) : (
        <Table
          ariaLabel={t({ id: 'settings.actions.actions-table.table.label', message: 'Actions' })}
          columnWidths={Object.values(columnWidths)}
          variant="grid"
          bordered
        >
          {(isLoading || !!sortedActionTypes?.length) && (
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.actions.actions-table.column.name">Name</Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions-table.column.product-categories">
                    Product categories
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions-table.column.quantifiable">
                    Quantifiable
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions-table.column.linked-defects">
                    Linked defects
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions-table.column.child-actions">
                    Child actions
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions-table.column.spare-parts">Spare parts</Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions-table.column.brands">Brands</Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions-table.column.additional-info-config">
                    Additional fields
                  </Trans>
                </Column>
                <Column
                  ariaLabel={t({
                    id: 'settings.actions.actions-table.column.edit-or-delete',
                    message: 'Edit or delete',
                  })}
                />
              </Row>
            </Header>
          )}

          <Body>
            {sortedActionTypes?.map((actionType) => (
              <ActionRow
                key={actionType.id}
                actionType={actionType}
                onEdit={() => {
                  setActionTypeToEdit(actionType);
                  setIsDrawerOpen(true);
                }}
              />
            ))}
          </Body>
        </Table>
      )}

      <ActionDrawer
        actionType={actionTypeToEdit}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);

          // Wait for the Drawer CSS exit animation to finish
          setTimeout(() => {
            setActionTypeToEdit(null);
          }, 150);
        }}
      />
    </Stack>
  );
};

const ActionRow = ({ actionType, onEdit }: { actionType: ActionType; onEdit: () => void }) => {
  const showToast = useShowToast();

  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  const [deleteConfirmationOpened, setDeleteConfirmationOpened] = useState(false);
  const { mutateAsync: deleteActionType, isPending: isDeleteActionTypePending } =
    useDeleteActionType();

  const uniqueLinkedDefectTypes = unique(
    actionType.actionTypeToDefectTypes.map(({ defectTypeOrganization }) => defectTypeOrganization),
    'id'
  );

  const defectTypeNames = (
    <Stack>
      {uniqueLinkedDefectTypes.map((defectType) => (
        <span key={defectType.id}>· {_db(defectType.name)}</span>
      ))}
    </Stack>
  );

  const additionalFieldsNames = (
    <Stack>
      {actionType.additionalInfoConfig?.map((element) => (
        <span key={element.id}>· {_db(element.config.label)}</span>
      ))}
    </Stack>
  );

  const childrenActionNames = (
    <Stack>
      {actionType.children.map((childAction) => (
        <span key={childAction.id}>· {_db(childAction.name)}</span>
      ))}
    </Stack>
  );

  const sparePartTypeNames = (
    <Stack>
      {actionType.sparePartTypes.map(({ sparePartTypeOrganization }) => (
        <span key={sparePartTypeOrganization.id}>· {_db(sparePartTypeOrganization.name)}</span>
      ))}
    </Stack>
  );

  const sortedBrands = actionType.brands.sort((a, b) => a.name.localeCompare(b.name));
  const brandNames = (
    <Stack>
      {sortedBrands.map((brand) => (
        <span key={brand.id}>· {brand.name}</span>
      ))}
    </Stack>
  );

  const actionTypeName = _db(actionType.name);

  return (
    <>
      <Row>
        <Cell>
          <Stack>
            <Stack row alignItems="center" gap="4px">
              <InlineText weight="medium">{actionTypeName} </InlineText>
              {actionType.refashionId && <RefashionLogo tooltip={actionType.refashionId} />}
            </Stack>
            <InlineText size="200" color="disabled">
              {actionType.id}
            </InlineText>
          </Stack>
        </Cell>
        <Cell>
          <ProductAttributeValuesHierarchy
            productAttributeValues={actionType.productAttributeValues}
          />
        </Cell>
        <Cell justifyContent="center">
          {actionType.isQuantifiable ? <IconCheck /> : <IconCross />}
        </Cell>
        <Cell justifyContent="center">
          <Tooltip
            content={
              uniqueLinkedDefectTypes.length
                ? defectTypeNames
                : t({
                    id: 'settings.actions.actions-table.no-defects',
                    message: 'No defects linked',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {uniqueLinkedDefectTypes.length}
            </Button>
          </Tooltip>
        </Cell>
        <Cell justifyContent="center">
          <Tooltip
            content={
              actionType.children.length
                ? childrenActionNames
                : t({
                    id: 'settings.actions.actions-table.no-child',
                    message: 'No child actions',
                  })
            }
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {actionType.children.length}
            </Button>
          </Tooltip>
        </Cell>
        <Cell justifyContent="center">
          <Tooltip
            content={
              actionType.sparePartTypes.length
                ? sparePartTypeNames
                : t({
                    id: 'settings.actions.actions-table.no-spare-parts',
                    message: 'No spare parts linked',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {actionType.sparePartTypes.length}
            </Button>
          </Tooltip>
        </Cell>
        <Cell justifyContent="center">
          <Tooltip
            content={
              actionType.brands.length
                ? brandNames
                : t({
                    id: 'settings.actions.actions-table.no-brands',
                    message: 'No brands',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {actionType.brands.length}
            </Button>
          </Tooltip>
        </Cell>
        <Cell justifyContent="center">
          <Tooltip
            content={
              actionType.additionalInfoConfig?.length
                ? additionalFieldsNames
                : t({
                    id: 'settings.actions.actions-table.no-additional-fields',
                    message: 'No additional fields',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {actionType.additionalInfoConfig?.length || 0}
            </Button>
          </Tooltip>
        </Cell>
        <Cell justifyContent="center">
          <Stack row gap="0.5rem" flexWrap="nowrap">
            <Button
              variant="secondary"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.actions.actions-table.edit', message: 'Edit' })}
              onPress={onEdit}
            >
              <IconEdit />
            </Button>
            <AgreementPricesDialog
              id={actionType.id}
              name={_db(actionType.name)}
              agreementPrices={actionType.agreementPrices}
              type="action"
              mode="brand"
            />
            <Button
              variant="danger"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.actions.actions-table.delete', message: 'Delete' })}
              onPress={() => setDeleteConfirmationOpened(true)}
            >
              <IconTrash />
            </Button>
          </Stack>
        </Cell>
      </Row>
      <ConfirmationDialog
        title={
          <Trans id="settings.actions.actions-table.delete.confirmation.title">Delete action</Trans>
        }
        confirm={
          <Trans id="settings.actions.actions-table.delete.confirmation.confirm">Delete</Trans>
        }
        onConfirm={() =>
          deleteActionType(actionType.id)
            .then(() => {
              showToast({
                text: (
                  <Trans id="settings.actions.actions-table.delete.confirmation.success">
                    The action <b>&quot;{actionTypeName}&quot;</b> has been deleted.
                  </Trans>
                ),
                type: 'success',
              });
            })
            .catch(() => {
              showToast({
                text: (
                  <Trans id="settings.actions.actions-table.delete.confirmation.error">
                    Error while deleting the action <b>&quot;{actionTypeName}&quot;</b>. Please try
                    again later.
                  </Trans>
                ),
                type: 'error',
              });
            })
        }
        showDialog={deleteConfirmationOpened}
        setShowDialog={setDeleteConfirmationOpened}
        maxWidth="35rem"
        isPending={isDeleteActionTypePending}
      >
        <Stack gap="0.5rem">
          <p>
            <Trans id="settings.actions.actions-table.delete.confirmation.description.1">
              Are you sure you want to delete the action <b>&quot;{actionTypeName}&quot;</b>?
            </Trans>
          </p>
          <p>
            <Trans id="settings.actions.actions-table.delete.confirmation.description.2">
              Active requests with this action will continue to show it, but users won't be able to
              choose this action for new requests.
            </Trans>
          </p>
          <hr />
          <p>
            <b>
              <Trans id="settings.actions.actions-table.delete.confirmation.description.3">
                This deletion cannot be undone.
              </Trans>
            </b>
          </p>
        </Stack>
      </ConfirmationDialog>
    </>
  );
};
